import React from 'react';
import { Link } from 'wouter';
import { Tool } from '../../types';
import { ImageWithFallback } from '../ImageWithFallback';
import { getToolLogoUrl } from '../../utils/tools/logos';
import { ArrowRight, Star, ExternalLink } from 'lucide-react';
import { generateCategorySlug } from '../../utils/slugs';

interface ToolCardProps {
  tool: Tool;
}

export function ToolCard({ tool }: ToolCardProps) {
  const toolSlug = tool.id || tool.name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
  const categorySlug = generateCategorySlug(tool.category);

  return (
    <div
      className="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-4 flex flex-col h-full border border-gray-100 hover:border-blue-100 relative"
      itemScope
      itemType="https://schema.org/SoftwareApplication"
    >
      {/* Hidden metadata for SEO */}
      <meta itemProp="name" content={tool.name} />
      <meta itemProp="description" content={tool.description} />
      <meta itemProp="applicationCategory" content={tool.category} />
      <meta itemProp="operatingSystem" content="Any" />
      <meta itemProp="url" content={tool.website} />
      
      <Link href={`/tools/${toolSlug}`}>
        <a className="flex items-center mb-3 cursor-pointer">
          <ImageWithFallback
            src={getToolLogoUrl(tool)}
            alt={`${tool.name} - ${tool.category} software`}
            toolId={tool.id}
            width={40}
            height={40}
            className="w-10 h-10 rounded-lg bg-white border border-gray-100 p-1 group-hover:scale-110 transition-transform"
            itemProp="image"
          />
          <div className="ml-3">
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors" itemProp="name">{tool.name}</h3>
            <Link href={`/categories/${categorySlug}`}>
              <a className="text-sm text-blue-600 hover:text-blue-700 hover:underline" itemProp="applicationCategory" onClick={(e) => e.stopPropagation()}>
                {tool.category}
              </a>
            </Link>
          </div>
        </a>
      </Link>
      
      <p className="text-gray-600 text-sm mb-3 flex-grow" itemProp="description">{tool.description}</p>
      
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
        <div className="flex space-x-1" itemProp="offers" itemScope itemType="https://schema.org/Offer">
          <meta itemProp="price" content={tool.pricing.includes('free') ? "0" : "10"} />
          <meta itemProp="priceCurrency" content="USD" />
          {tool.pricing && tool.pricing.map((price) => (
            <span
              key={price}
              className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600"
            >
              {price === 'free' ? 'Free' :
               price === 'low' ? '$' :
               price === 'medium' ? '$$' : '$$$'}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link href={`/tools/${toolSlug}`}>
            <a className="text-sm text-blue-600 flex items-center hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded" aria-label={`Learn more about ${tool.name}`}>
              Learn more
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </a>
          </Link>
          <a
            href={tool.website}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${tool.name} website (opens in new tab)`}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>
      
      {/* Recommended badge for top tools */}
      {tool.bestFor && tool.bestFor.length > 2 && (
        <div className="absolute top-2 right-2 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center">
          <Star className="w-3 h-3 mr-1 text-yellow-500 fill-yellow-500" />
          Popular
        </div>
      )}
    </div>
  );
}

export default ToolCard;